import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Chrome, Copy, Download, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;
export const EXTENSION_ZIP = '/skipya-extension.zip';

// Pairing code for the impots.gouv.fr extension + list of connected extensions.
export const ExtensionPairing = () => {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };
  const muted = isDark ? 'text-[#94A3B8]' : 'text-gray-600';

  const loadSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/extension/sessions`, { headers: { Authorization: `Bearer ${token}` } });
      setSessions(r.data.sessions || []);
    } catch {
      setSessions([]);
    }
  }, [token]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const createCode = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/extension/pairing-code`, {}, { headers });
      setCode(r.data);
    } catch {
      toast.error('Impossible de générer le code');
    } finally {
      setLoading(false);
    }
  };

  const revoke = async (jti) => {
    try {
      await axios.delete(`${API}/api/extension/sessions/${jti}`, { headers });
      toast.success('Extension déconnectée');
      loadSessions();
    } catch {
      toast.error('Déconnexion impossible');
    }
  };

  return (
    <section
      id="extension"
      data-testid="extension-pairing"
      className={`rounded-2xl border p-5 mb-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}
    >
      <div className="w-11 h-11 rounded-xl bg-[#81d8d0]/15 flex items-center justify-center mb-5">
        <Chrome size={22} className="text-[#81d8d0]" />
      </div>
      <h2 className="text-xl font-semibold mb-2">Extension impots.gouv.fr</h2>
      <p className={`text-sm leading-6 mb-4 ${muted}`}>
        L'extension affiche ta checklist Skipya à côté de ta déclaration et te permet de copier chaque
        montant. Elle ne remplit et ne valide rien à ta place : la vérification finale reste la tienne.
      </p>
      <ol className={`text-sm leading-6 mb-4 list-decimal pl-5 ${muted}`}>
        <li>Télécharge l'extension, décompresse-la, puis dans Chrome ouvre <code>chrome://extensions</code>, active le « mode développeur » et clique « Charger l'extension non empaquetée ».</li>
        <li>Génère un code ci-dessous et saisis-le dans l'extension (valable 10 minutes).</li>
      </ol>
      <div className="flex flex-wrap gap-3 mb-4">
        <a
          href={EXTENSION_ZIP}
          download
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#81d8d0] text-black"
          data-testid="extension-download"
        >
          <Download size={16} /> Télécharger l'extension
        </a>
        <button
          type="button"
          onClick={createCode}
          disabled={loading}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}
          data-testid="extension-create-code"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          Générer un code de connexion
        </button>
      </div>
      {code && (
        <div className="rounded-xl p-4 mb-4 bg-[#81d8d0]/10 border border-[#81d8d0]/30 flex items-center justify-between gap-3">
          <div>
            <div className={`text-xs ${muted}`}>Code à saisir dans l'extension</div>
            <div className="text-2xl font-mono tracking-[0.3em]" data-testid="extension-code">{code.code}</div>
          </div>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(code.code).then(() => toast.success('Code copié'))}
            className="p-2 rounded-lg hover:bg-[#81d8d0]/20"
            aria-label="Copier le code"
          >
            <Copy size={18} />
          </button>
        </div>
      )}
      <h3 className="text-sm font-semibold mb-2">Extensions connectées</h3>
      {sessions.length === 0 ? (
        <p className={`text-sm ${muted}`}>Aucune extension connectée.</p>
      ) : (
        <ul className="space-y-2">
          {sessions.map((s) => (
            <li key={s.jti} className="flex items-center justify-between text-sm">
              <span>
                {s.device_label} · connectée le {new Date(s.created_at).toLocaleDateString('fr-FR')}
                {s.last_used_at ? ` · utilisée le ${new Date(s.last_used_at).toLocaleDateString('fr-FR')}` : ''}
              </span>
              <button type="button" onClick={() => revoke(s.jti)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500" aria-label="Déconnecter">
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ExtensionPairing;
